import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Target, Trophy, Calendar, FileText, Award, 
  Flame, Star, ChevronRight, TrendingUp, Clock, CheckCircle,
  Smile, Meh, Frown, AlertCircle, BookOpen, Sparkles,
  MessageSquare, Eye, Brain
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOOD_ICONS = {
  great: { icon: Smile, color: 'text-green-500', label: 'Great', value: 5 },
  good: { icon: Smile, color: 'text-lime-500', label: 'Good', value: 4 },
  okay: { icon: Meh, color: 'text-yellow-500', label: 'Okay', value: 3 },
  struggling: { icon: Frown, color: 'text-orange-500', label: 'Struggling', value: 2 },
  tough: { icon: AlertCircle, color: 'text-red-500', label: 'Tough Week', value: 1 }
};

const TIER_COLORS = {
  bronze: 'bg-amber-600',
  silver: 'bg-gray-400',
  gold: 'bg-yellow-500',
  diamond: 'bg-cyan-500'
};

// Goal Card Component with text truncation
const GoalCard = ({ goal, colorClass }) => {
  const [expanded, setExpanded] = useState(false);
  const MAX_LENGTH = 100;
  
  const hasLongText = (goal.specific && goal.specific.length > MAX_LENGTH) || 
                      (goal.measurable && goal.measurable.length > MAX_LENGTH) ||
                      (goal.achievable && goal.achievable.length > MAX_LENGTH);
  
  const truncate = (text) => {
    if (!text) return '';
    if (text.length <= MAX_LENGTH || expanded) return text;
    return text.substring(0, MAX_LENGTH) + '...';
  };

  return (
    <Card className="p-3 rounded-xl">
      <div className="flex items-center justify-between mb-2">
        <p className="font-medium text-sm">{goal.title}</p>
        <Badge className={goal.status === 'completed' ? 'bg-green-500' : colorClass}>
          {goal.status === 'completed' ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
          {goal.progress || 0}%
        </Badge>
      </div>
      <Progress value={goal.progress || 0} className="h-2" />
      
      {/* SMART Breakdown - truncated */}
      {(goal.specific || goal.measurable || goal.achievable || goal.relevant || goal.deadline) && (
        <div className="mt-3 space-y-1 text-xs">
          {goal.specific && (
            <p className="text-muted-foreground">
              <span className="font-medium text-gray-700">S:</span> {truncate(goal.specific)}
            </p>
          )}
          {expanded && (
            <>
              {goal.measurable && (
                <p className="text-muted-foreground">
                  <span className="font-medium text-gray-700">M:</span> {goal.measurable}
                </p>
              )}
              {goal.achievable && (
                <p className="text-muted-foreground">
                  <span className="font-medium text-gray-700">A:</span> {goal.achievable}
                </p>
              )}
              {goal.relevant && (
                <p className="text-muted-foreground">
                  <span className="font-medium text-gray-700">R:</span> {goal.relevant}
                </p>
              )}
              {goal.deadline && (
                <p className="text-muted-foreground">
                  <span className="font-medium text-gray-700">T:</span> {new Date(goal.deadline).toLocaleDateString()}
                </p>
              )}
            </>
          )}
          {hasLongText && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-primary hover:underline font-medium mt-1"
            >
              {expanded ? 'Show less' : 'Show more...'}
            </button>
          )}
        </div>
      )}
    </Card>
  );
};

const AIConversationsTab = ({ youthId }) => {
  const [convos, setConvos] = useState([]);
  const [loadingAI, setLoadingAI] = useState(true);

  useEffect(() => {
    const fetchConvos = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ai/conversations/${youthId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) { const d = await res.json(); setConvos(d.conversations || []); }
      } catch (e) {}
      setLoadingAI(false);
    };
    if (youthId) fetchConvos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youthId]);

  if (loadingAI) return <div className="text-center py-8 text-gray-400">Loading AI conversations...</div>;

  return convos.length > 0 ? (
    <div className="space-y-3">
      {convos.map((c, i) => (
        <Card key={c.id || i} className="p-4 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-violet-100 text-violet-700 text-[10px]">{(c.type || 'chat').replace(/_/g, ' ')}</Badge>
            {c.model && <Badge className="bg-gray-100 text-gray-500 text-[10px]">{c.model}</Badge>}
            <span className="text-xs text-gray-400 ml-auto">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
          </div>
          <div className="space-y-2">
            <div className="p-2 rounded-lg bg-blue-50">
              <p className="text-xs font-semibold text-blue-700 mb-0.5">Youth asked:</p>
              <p className="text-sm text-blue-900">{c.user_message}</p>
            </div>
            <div className="p-2 rounded-lg bg-violet-50">
              <p className="text-xs font-semibold text-violet-700 mb-0.5">AI responded:</p>
              <p className="text-sm text-violet-900 whitespace-pre-wrap">{(c.ai_response || '').substring(0, 300)}{(c.ai_response || '').length > 300 ? '...' : ''}</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ) : (
    <Card className="p-8 text-center rounded-2xl">
      <Brain className="w-12 h-12 text-gray-300 mx-auto mb-2" />
      <p className="text-muted-foreground">No AI conversations yet</p>
    </Card>
  );
};

const YouthDetailPage = ({ user }) => {
  const navigate = useNavigate();
  const { youthId } = useParams();
  const [youth, setYouth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [goals, setGoals] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [badges, setBadges] = useState([]);
  const [wins, setWins] = useState([]);
  const [project, setProject] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [moodTrend, setMoodTrend] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [careers, setCareers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [youthStats, setYouthStats] = useState(null);
  const [leadMonitor, setLeadMonitor] = useState(null);

  useEffect(() => {
    const allowedRoles = ['lead_monitor', 'coach', 'admin', 'program_admin', 'platform_admin'];
    if (!allowedRoles.includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchYouthData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youthId]);

  const fetchYouthData = async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch comprehensive youth profile
      const fullRes = await fetch(`${API_URL}/api/monitor/youth/${youthId}/full-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!fullRes.ok) {
        toast.error('Could not load youth data');
        navigate(-1);
        return;
      }
      
      const fullData = await fullRes.json();
      setYouth(fullData.youth);
      setGoals(fullData.goals || []);
      setWins(fullData.wins || []);
      setAssessments(fullData.assessments || []);
      setCareers(fullData.career_explorations || []);
      setCourses(fullData.courses || []);
      setQuestions(fullData.questions || []);
      setYouthStats(fullData.stats);
      setBadges(fullData.badges || []);
      setLeadMonitor(fullData.lead_monitor);
      
      const checkinsData = fullData.checkins || [];
      setCheckins(checkinsData);
      const trend = checkinsData.map(c => ({
        week: `Week ${c.week_number}`,
        mood: MOOD_ICONS[c.mood]?.value || 3,
        moodLabel: c.mood
      })).reverse();
      setMoodTrend(trend);
      
      // Also fetch highlights separately
      const highlightsRes = await fetch(`${API_URL}/api/monitor/youth/${youthId}/highlights`, { headers: { Authorization: `Bearer ${token}` } });
      if (highlightsRes.ok) setHighlights(await highlightsRes.json());
      
      // Fetch project
      const projectRes = await fetch(`${API_URL}/api/monitor/youth/${youthId}/project`, { headers: { Authorization: `Bearer ${token}` } });
      if (projectRes.ok) setProject(await projectRes.json());
      
    } catch (error) {
      console.error('Error fetching youth data:', error);
      toast.error('Error loading data');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  if (!youth) {
    return null;
  }

  const summerGoals = goals.filter(g => g.goal_type === 'summer');
  const longTermGoals = goals.filter(g => g.goal_type === 'long_term');
  const completedGoals = goals.filter(g => g.status === 'completed').length;
  const latestCheckin = checkins[0];

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white rounded-b-3xl">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20 rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <h1 className="font-heading text-2xl font-bold">Youth Detail View</h1>
          </div>

          {/* Youth Profile Card */}
          <div className="bg-white/10 backdrop-blur rounded-2xl p-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 ring-4 ring-white/30">
                {youth.profile_picture ? (
                  <AvatarImage src={youth.profile_picture} alt={youth.name} />
                ) : null}
                <AvatarFallback className="bg-white/20 text-white font-heading text-2xl font-bold">
                  {youth.avatar_emoji || youth.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold">{youth.name}</h2>
                <p className="text-white/80 text-sm">{youth.email}</p>
                {youth.phone && <p className="text-white/70 text-xs">{youth.phone}</p>}
                {youth.secondary_email && <p className="text-white/60 text-xs">{youth.secondary_email}</p>}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {youth.program_type && (
                    <Badge className={`${youth.program_type === 'upnext' ? 'bg-green-500' : 'bg-purple-500'} text-white text-xs`}>
                      {youth.program_type === 'upnext' ? 'UpNEXT' : 'NextGEN'}
                    </Badge>
                  )}
                  {youth.work_site && (
                    <Badge className="bg-white/20 text-white text-xs">{youth.work_site}</Badge>
                  )}
                  {youth.school && (
                    <span className="text-xs text-white/70">{youth.school}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-4">
              <div className="text-center">
                <p className="text-xl font-bold">{youth.total_points || 0}</p>
                <p className="text-[10px] text-white/70">Points</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold">{badges.length}</p>
                <p className="text-[10px] text-white/70">Badges</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold">{youth.current_streak || 0}</p>
                <p className="text-[10px] text-white/70">Streak</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold">{completedGoals}/{goals.length}</p>
                <p className="text-[10px] text-white/70">Goals</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold">{checkins.length}</p>
                <p className="text-[10px] text-white/70">Check-ins</p>
              </div>
            </div>
            {leadMonitor && (
              <div className="mt-3 bg-white/10 rounded-xl px-3 py-2 text-xs text-white/80">
                Lead Monitor: <strong>{leadMonitor.name}</strong> ({leadMonitor.email})
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="overflow-x-auto flex-nowrap w-full justify-start h-12 rounded-xl bg-gray-100 p-1" style={{ scrollbarWidth: 'none' }}>
            <TabsTrigger value="overview" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="checkins" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-checkins">Check-ins</TabsTrigger>
            <TabsTrigger value="goals" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-goals">Goals</TabsTrigger>
            <TabsTrigger value="careers" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-careers">Careers</TabsTrigger>
            <TabsTrigger value="courses" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="project" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-project">Project</TabsTrigger>
            <TabsTrigger value="wins" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-wins">Wins</TabsTrigger>
            <TabsTrigger value="assessments" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-assessments">Assessments</TabsTrigger>
            <TabsTrigger value="questions" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-questions">Questions</TabsTrigger>
            <TabsTrigger value="ai-history" className="rounded-lg text-xs flex-shrink-0 whitespace-nowrap" data-testid="tab-ai-chats">AI Chats</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Latest Check-in Alert */}
            {latestCheckin && (
              <Card className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${MOOD_ICONS[latestCheckin.mood]?.color || 'text-gray-500'} bg-white`}>
                    {(() => {
                      const MoodIcon = MOOD_ICONS[latestCheckin.mood]?.icon || Meh;
                      return <MoodIcon className="w-6 h-6" />;
                    })()}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">Week {latestCheckin.week_number} Check-in</p>
                    <p className="text-xs text-muted-foreground">
                      Mood: {MOOD_ICONS[latestCheckin.mood]?.label || latestCheckin.mood} • {new Date(latestCheckin.created_at).toLocaleDateString()}
                    </p>
                    {latestCheckin.highlight && (
                      <p className="text-sm mt-2 text-gray-700">"{latestCheckin.highlight}"</p>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Mood Trend Chart */}
            {moodTrend.length > 1 && (
              <Card className="p-4 rounded-2xl">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-500" />
                  Mood Trend
                </h3>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={moodTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                      <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 10 }} />
                      <Tooltip 
                        formatter={(value, name) => [MOOD_ICONS[Object.keys(MOOD_ICONS).find(k => MOOD_ICONS[k].value === value)]?.label || value, 'Mood']}
                      />
                      <Line type="monotone" dataKey="mood" stroke="#6366f1" strokeWidth={2} dot={{ fill: '#6366f1' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}

            {/* Badges */}
            <Card className="p-4 rounded-2xl">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Earned Badges ({badges.length})
              </h3>
              {badges.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {badges.map((badge, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-full px-3 py-1">
                      <span className={`w-6 h-6 rounded-full ${TIER_COLORS[badge.tier]} flex items-center justify-center text-xs`}>
                        {badge.icon}
                      </span>
                      <span className="text-sm font-medium">{badge.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No badges earned yet</p>
              )}
            </Card>

            {/* Recent Highlights */}
            <Card className="p-4 rounded-2xl">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Recent Highlights ({highlights.length})
              </h3>
              {highlights.length > 0 ? (
                <div className="space-y-2">
                  {highlights.slice(0, 5).map((h, idx) => (
                    <div key={idx} className="bg-purple-50 rounded-xl p-3">
                      <p className="text-sm">{h.content}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(h.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No highlights yet</p>
              )}
            </Card>
          </TabsContent>

          {/* Check-ins Tab */}
          <TabsContent value="checkins" className="space-y-4 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Weekly Check-ins ({checkins.length})
            </h3>
            {checkins.length > 0 ? (
              <div className="space-y-3">
                {checkins.map((checkin, idx) => {
                  const MoodIcon = MOOD_ICONS[checkin.mood]?.icon || Meh;
                  return (
                    <Card key={idx} className="p-4 rounded-2xl">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${MOOD_ICONS[checkin.mood]?.color || 'text-gray-500'} bg-gray-100`}>
                          <MoodIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold">Week {checkin.week_number}</p>
                            <Badge variant="outline" className="text-xs">
                              {MOOD_ICONS[checkin.mood]?.label || checkin.mood}
                            </Badge>
                          </div>
                          {checkin.highlight && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500">Highlight:</p>
                              <p className="text-sm">{checkin.highlight}</p>
                            </div>
                          )}
                          {checkin.challenge && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500">Challenge:</p>
                              <p className="text-sm">{checkin.challenge}</p>
                            </div>
                          )}
                          {checkin.learning && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500">What I Learned:</p>
                              <p className="text-sm">{checkin.learning}</p>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">
                            Submitted: {new Date(checkin.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="p-8 text-center rounded-2xl">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">No check-ins submitted yet</p>
              </Card>
            )}
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Target className="w-5 h-5 text-green-500" />
                Goals ({goals.length})
              </h3>
              <Badge variant="outline">
                {completedGoals} completed
              </Badge>
            </div>
            
            {/* Summer Goals */}
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Summer Goals ({summerGoals.length})</p>
              {summerGoals.length > 0 ? (
                <div className="space-y-2">
                  {summerGoals.map((goal, idx) => (
                    <GoalCard key={idx} goal={goal} colorClass="bg-blue-500" />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No summer goals set</p>
              )}
            </div>

            {/* Long-term Goals */}
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Long-term Goals ({longTermGoals.length})</p>
              {longTermGoals.length > 0 ? (
                <div className="space-y-2">
                  {longTermGoals.map((goal, idx) => (
                    <GoalCard key={idx} goal={goal} colorClass="bg-purple-500" />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No long-term goals set</p>
              )}
            </div>
          </TabsContent>

          {/* Project Tab */}
          <TabsContent value="project" className="space-y-4 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              Summer Project
            </h3>
            {project ? (
              <Card className="p-4 rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold">{project.project_name}</p>
                    <p className="text-sm text-muted-foreground">{project.project_id}</p>
                  </div>
                  <Badge className={project.status === 'completed' ? 'bg-green-500' : 'bg-orange-500'}>
                    {project.status === 'completed' ? 'Completed' : 'In Progress'}
                  </Badge>
                </div>
                <Progress value={project.progress || 0} className="h-3 mb-2" />
                <p className="text-sm text-right text-muted-foreground">{project.progress || 0}% Complete</p>
                
                {/* Completed Steps */}
                {project.completed_steps && project.completed_steps.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Completed Steps ({project.completed_steps.length})</p>
                    <div className="space-y-2">
                      {project.completed_steps.map((step, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>Step {step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project Responses (actual answers) */}
                {project.responses && Object.keys(project.responses).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Youth's Responses</p>
                    <div className="space-y-3">
                      {Object.entries(project.responses).map(([key, value]) => (
                        value && String(value).trim() && (
                          <div key={key} className="bg-gray-50 rounded-xl p-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                            <p className="text-sm">{String(value)}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {project.submitted_at && (
                  <p className="text-xs text-green-600 mt-3">Submitted: {new Date(project.submitted_at).toLocaleDateString()}{project.is_late ? ' (Late)' : ''}</p>
                )}
              </Card>
            ) : (
              <Card className="p-8 text-center rounded-2xl">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">No project selected yet</p>
              </Card>
            )}
          </TabsContent>

          {/* Wins Tab */}
          <TabsContent value="wins" className="space-y-4 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Wins & Celebrations ({wins.length})
            </h3>
            {wins.length > 0 ? (
              <div className="space-y-3">
                {wins.map((win, idx) => (
                  <Card key={idx} className="p-4 rounded-2xl">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center text-xl">
                        🏆
                      </div>
                      <div className="flex-1">
                        <Badge className={win.post_type === 'goal_completed' ? 'bg-green-500' : 'bg-purple-500'} >
                          {win.post_type === 'goal_completed' ? '🎯 Goal Completed' : '🏆 Win'}
                        </Badge>
                        <p className="font-medium mt-2">{win.title}</p>
                        {win.description && (
                          <p className="text-sm text-muted-foreground mt-1">{win.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(win.created_at).toLocaleDateString()} • {win.celebrations?.length || 0} celebrations
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center rounded-2xl">
                <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">No wins posted yet</p>
              </Card>
            )}
          </TabsContent>

          {/* AI Conversations Tab */}
          <TabsContent value="ai-history" className="space-y-4 mt-4">
            <AIConversationsTab youthId={youthId} />
          </TabsContent>

          {/* Careers Tab */}
          <TabsContent value="careers" className="space-y-4 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Brain className="w-5 h-5 text-orange-500" />
              Career Explorations ({careers.length})
            </h3>
            {careers.length > 0 ? (
              <div className="space-y-3">
                {careers.map((c, idx) => (
                  <Card key={idx} className="p-4 rounded-2xl">
                    <p className="font-semibold">{c.career_title || c.title || 'Career'}</p>
                    {c.industry && <p className="text-sm text-muted-foreground">{c.industry}</p>}
                    {c.salary_range && <p className="text-xs text-green-600 mt-1">{c.salary_range}</p>}
                    {c.interest_level && <Badge className="mt-2 bg-orange-100 text-orange-700">{c.interest_level}</Badge>}
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center rounded-2xl">
                <Brain className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">No careers explored yet</p>
              </Card>
            )}
            {youth.interests?.length > 0 && (
              <Card className="p-4 rounded-2xl">
                <p className="text-sm font-medium mb-2">Interests</p>
                <div className="flex flex-wrap gap-1.5">
                  {youth.interests.map((i, idx) => (
                    <Badge key={idx} className="bg-pink-50 text-pink-700 border border-pink-200 text-xs">{String(i).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-4 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" />
              Course Progress ({courses.length})
            </h3>
            {courses.length > 0 ? (
              <div className="space-y-3">
                {courses.map((c, idx) => (
                  <Card key={idx} className="p-4 rounded-2xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-sm">{c.course_id?.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()) || 'Course'}</p>
                      <Badge className={c.completed ? 'bg-green-500 text-white' : 'bg-blue-100 text-blue-700'}>
                        {c.completed ? 'Completed' : `Module ${c.current_module || 0}`}
                      </Badge>
                    </div>
                    <Progress value={c.completed ? 100 : ((c.current_module || 0) / (c.total_modules || 10)) * 100} className="h-2" />
                    <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                      <span>{c.xp_earned || 0} XP earned</span>
                      <span>{c.badges_earned?.length || 0} badges</span>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center rounded-2xl">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">No courses started yet</p>
              </Card>
            )}
          </TabsContent>

          {/* Assessments Tab */}
          <TabsContent value="assessments" className="space-y-4 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Star className="w-5 h-5 text-violet-500" />
              Assessments & Surveys ({assessments.length})
            </h3>
            {assessments.length > 0 ? (
              <div className="space-y-3">
                {assessments.map((a, idx) => (
                  <Card key={idx} className="p-4 rounded-2xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-sm">{a.assessment_type === 'pre' ? 'Pre-Summer Assessment' : a.assessment_type === 'post' ? 'Post-Summer Assessment' : a.assessment_type}</p>
                      <Badge className="bg-violet-100 text-violet-700">{a.total_score != null ? `Score: ${a.total_score}` : 'Completed'}</Badge>
                    </div>
                    {a.domain_scores && Object.keys(a.domain_scores).length > 0 && (
                      <div className="space-y-2 mt-2">
                        {Object.entries(a.domain_scores).map(([domain, data]) => (
                          <div key={domain}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="font-medium">{domain}</span>
                              <span>{typeof data === 'object' ? `${data.score}/${data.max}` : data}</span>
                            </div>
                            <Progress value={typeof data === 'object' ? (data.score / data.max) * 100 : 50} className="h-1.5" />
                          </div>
                        ))}
                      </div>
                    )}
                    {a.ai_feedback?.strengths && (
                      <div className="mt-3 bg-green-50 rounded-lg p-2">
                        <p className="text-xs font-medium text-green-700">Strengths: {a.ai_feedback.strengths.join(', ')}</p>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center rounded-2xl">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">No assessments taken yet</p>
              </Card>
            )}
          </TabsContent>

          {/* Questions Tab */}
          <TabsContent value="questions" className="space-y-4 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              Questions Asked ({questions.length})
            </h3>
            {questions.length > 0 ? (
              <div className="space-y-3">
                {questions.map((q, idx) => (
                  <Card key={idx} className="p-4 rounded-2xl">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-sm flex-1">{q.question}</p>
                      <Badge className={q.status === 'answered' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'} >
                        {q.status === 'answered' ? 'Answered' : 'Pending'}
                      </Badge>
                    </div>
                    {q.answer && (
                      <div className="bg-blue-50 rounded-lg p-2 mt-2">
                        <p className="text-xs text-blue-700">{q.answer}</p>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">{q.category || 'General'} • {q.created_at ? new Date(q.created_at).toLocaleDateString() : ''}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center rounded-2xl">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">No questions asked yet</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Navigation role={user.role} />
    </div>
  );
};

export default YouthDetailPage;
